import Permissions from "../components/users/rols/models/permissions";
import { RolsType } from "../components/users/rols/interface/Rols";

/**
 * Traduce una ruta de `/home/*` al módulo del catálogo de permisos que la
 * sirve, para poder exigir `ACCESS` sobre ese módulo antes de montarla.
 *
 * La resolución es por el primer segmento después de `/home`, comparado contra
 * el `path` que cada módulo declara en `permissions.tsx`. Los subsegmentos no
 * importan: `/home/clients/places` y `/home/clients/groups` pertenecen los dos
 * al módulo `clients`, y las pestañas de adentro ya tienen sus propios `Can`.
 *
 * Una ruta que no resuelve a ningún módulo queda sin vigilar. Es deliberado:
 * el catálogo no cubre todo (`/home/dashboard`, `/home/changepassword`,
 * `/home/analysis`, `/home/statistics/*`), y ante una ruta desconocida es
 * preferible dejarla pasar —como está hoy— que dejar gente encerrada fuera de
 * pantallas que sí le corresponden.
 */

/**
 * Rutas que sirven a un módulo pero cuyo primer segmento no coincide con el
 * `path` del catálogo. Sin esta tabla quedarían sin vigilar.
 */
const ROUTE_ALIASES: Record<string, string> = {
  // Alertas en hoja de cálculo, misma herramienta que /home/alerts.
  "alerts-vo": "alerts",
};

const firstSegment = (path: string): string =>
  path.replace(/^\/+/, "").split("/")[0].toLowerCase();

/** `id` y `key` de un módulo, que no siempre coinciden (`bi-reports` / `bi`). */
const subjectsOf = (modulo: RolsType): string[] =>
  [modulo.id, modulo.key].filter(
    (subject, index, list) => !!subject && list.indexOf(subject) === index
  );

/**
 * segmento -> subjects a los que puede exigirse `ACCESS`.
 *
 * Un segmento reclamado por más de un módulo se descarta. Hoy pasa con
 * `/dashboard`, que `ai` y `authorization` declaran como su `path` aunque no
 * son la pantalla de inicio: vigilarlo dejaría sin landing a todo el mundo.
 */
const SEGMENT_SUBJECTS: Map<string, string[]> = (() => {
  const claims = new Map<string, RolsType[]>();

  Permissions.forEach((modulo) => {
    if (!modulo?.path) return;
    const segment = firstSegment(modulo.path);
    if (!segment) return;
    claims.set(segment, [...(claims.get(segment) ?? []), modulo]);
  });

  const resolved = new Map<string, string[]>();

  claims.forEach((modules, segment) => {
    if (modules.length !== 1) return;
    resolved.set(segment, subjectsOf(modules[0]));
  });

  Object.keys(ROUTE_ALIASES).forEach((segment) => {
    const target = ROUTE_ALIASES[segment];
    const modulo = Permissions.find(
      (item) => item.id === target || item.key === target
    );
    if (modulo) resolved.set(segment.toLowerCase(), subjectsOf(modulo));
  });

  return resolved;
})();

/**
 * Subjects que hay que poder acceder para entrar a `pathname`, o `null` si la
 * ruta no pertenece a ningún módulo del catálogo y por tanto no se vigila.
 */
export const resolveModuleSubjects = (pathname: string): string[] | null => {
  if (!pathname.startsWith("/home")) return null;
  const segment = firstSegment(pathname.slice("/home".length));
  if (!segment) return null;
  return SEGMENT_SUBJECTS.get(segment) ?? null;
};
